import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';

import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';

@Component({
    selector: 'app-terminal',
    templateUrl: './terminal.component.html',
    styleUrls: ['./terminal.component.scss'],
    standalone: false
})
export class TerminalComponent implements AfterViewInit, OnDestroy
{
    @ViewChild('terminal', { static: true })
    private terminalHost!: ElementRef<HTMLDivElement>;

    private terminal!: Terminal;
    private fitAddon!: FitAddon;

    private inputBuffer = "";
    private cursorPos = 0;

    private history: string[] = [];
    private historyIndex = -1;
    private currentDraft = '';

    @Output()
    public commandExecuted = new EventEmitter<string>();

    public ngAfterViewInit(): void
    {
        this.fitAddon = new FitAddon();

        this.terminal = new Terminal({
            cursorBlink: true,
            fontFamily: 'Consolas, monospace',
            fontSize: 14,
            scrollback: 1000
        });

        this.terminal.loadAddon(this.fitAddon);
        this.terminal.open(this.terminalHost.nativeElement);
        this.fitAddon.fit();

        this.writePrompt();
        this.registerInputHandler();
    }

    public ngOnDestroy(): void
    {
        this.terminal.dispose();
    }

    public writeLine(text: string): void
    {
        this.terminal.writeln(text);
        this.writePrompt();
    }

    private writePrompt(): void
    {
        this.terminal.write('> ');
    }

    private registerInputHandler(): void
    {
        this.terminal.onData((data: string) =>
        {
            console.log(this.inputBuffer.length + ": " + this.inputBuffer);
            switch (data)
            {
                case '\r':
                    this.handleEnter();
                    break;

                case '\u007F':
                    this.handleBackspace();
                    break;

                case '\x1b[3~': 
                    this.handleDelete();
                    break;

                case '\x1b[A': 
                    this.navigateHistoryUp();
                    break;

                case '\x1b[B':
                    this.navigateHistoryDown();
                    break;

                case '\x1b[D': 
                    this.moveCursorLeft();
                    break;

                case '\x1b[C': 
                    this.moveCursorRight();
                    break;

                default:
                    this.handleTextInput(data);
                    break;
            }
        });
    }

    private navigateHistoryUp(): void
{
    if (this.history.length === 0)
    {
        return;
    }

    if (this.historyIndex === -1)
    {
        // erster ↑: Draft sichern
        this.currentDraft = this.inputBuffer;
        this.historyIndex = this.history.length - 1;
    }
    else if (this.historyIndex > 0)
    {
        this.historyIndex--;
    }

    this.replaceInput(this.history[this.historyIndex]);
}

private navigateHistoryDown(): void
{
    if (this.historyIndex === -1)
    {
        return;
    }

    if (this.historyIndex < this.history.length - 1)
    {
        this.historyIndex++;
        this.replaceInput(this.history[this.historyIndex]);
    }
    else
    {
        // zurück zum Draft
        this.historyIndex = -1;
        this.replaceInput(this.currentDraft);
    }
}

private replaceInput(text: string): void
{
    // aktuelle Eingabe löschen
    while (this.inputBuffer.length > 0)
    {
        this.terminal.write('\b \b');
        this.inputBuffer = this.inputBuffer.slice(0, -1);
    }

    // neuen Text schreiben
    this.inputBuffer = text;
    this.terminal.write("> " +text);
}


    private async handleEnter(): Promise<void>
    {
        const command = this.inputBuffer.trim();

        this.terminal.write('\r\n');

        if (command.length > 0)
        {
            this.history.push(command);
            this.commandExecuted.emit(command);
        }

        this.inputBuffer = '';
        this.historyIndex = -1;
        this.currentDraft = '';

        this.writePrompt();
    }

    private handleBackspace(): void
    {
        if (this.cursorPos === 0)
        {
            return;
        }

        this.inputBuffer =
            this.inputBuffer.slice(0, this.cursorPos - 1) +
            this.inputBuffer.slice(this.cursorPos);

        this.cursorPos--;

        this.redrawLine();
    }

    private handleDelete(): void
    {
        if (this.cursorPos >= this.inputBuffer.length)
        {
            return;
        }

        this.inputBuffer =
            this.inputBuffer.slice(0, this.cursorPos) +
            this.inputBuffer.slice(this.cursorPos + 1);

        this.redrawLine();
    }

    private moveCursorLeft(): void
    {
        if (this.cursorPos > 0)
        {
            this.cursorPos--;
            this.terminal.write('\x1b[D'); // Cursor left
        }
    }

    private moveCursorRight(): void
    {
        if (this.cursorPos < this.inputBuffer.length)
        {
            this.cursorPos++;
            this.terminal.write('\x1b[C'); // Cursor right
        }
    }

    private handleTextInput(data: string): void
    {
        this.inputBuffer += data;
        this.terminal.write(data);
        this.cursorPos++;
    }

    private redrawLine(): void
    {
        // Cursor an Zeilenanfang
        this.terminal.write('\r');

        // Zeile leeren
        this.terminal.write('\x1b[2K');

        // Inhalt neu schreiben
        this.terminal.write("> " + this.inputBuffer);

        // Cursor wieder korrekt positionieren
        const moveLeft = this.inputBuffer.length - this.cursorPos;
        if (moveLeft > 0)
        {
            this.terminal.write(`\x1b[${moveLeft}D`);
        }
    }

}
